import { useCallback, useEffect, useState } from "react";
import { adminApiService } from "../utils/api";
import { GfFigmaTemplateData } from "@swagger/typescript-fetch-goflyer";

export interface UseFigmaTemplateSelectionOptions {
  pageSize?: number;
  initialPage?: number;
  relations?: string[];
  /** Store IDs whose specific templates should be fetched first (non-paginated) */
  storeIds?: string[];
}

interface TemplatePageState {
  loading: boolean;
  error?: string;
  /** Combined list (specific first, then public) */
  items: GfFigmaTemplateData[];
  page: number;
  total: number;
  hasMore: boolean;
  /** Fetched store-specific templates */
  specific: GfFigmaTemplateData[];
  /** Whether specific templates have been loaded */
  specificLoaded: boolean;
}

export const useFigmaTemplateSelection = ({
  pageSize = 20,
  initialPage = 1,
  relations = ["image", "category"],
  storeIds = [],
}: UseFigmaTemplateSelectionOptions = {}) => {
  const [state, setState] = useState<TemplatePageState>({
    loading: false,
    items: [],
    page: initialPage,
    total: 0,
    hasMore: true,
    specific: [],
    specificLoaded: false,
  });
  const [selected, setSelected] = useState<GfFigmaTemplateData | undefined>();
  const [refreshKey, setRefreshKey] = useState(0);

  /**
   * Fetch store-specific templates (non paginated, assumed small) then first/next page of public templates.
   */
  const fetchTemplates = useCallback(
    async (page: number, options?: { reset?: boolean }) => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        let specific: GfFigmaTemplateData[] = state.specific;
        let specificLoaded = state.specificLoaded;
        console.log("storeIds", storeIds);
        // Phase 1: fetch specific templates only once (or on reset/refresh)
        if ((storeIds?.length && !specificLoaded) || options?.reset) {
          try {
            const filterSpecific: string[] = [
              "type||$eq||specific",
              // Ensure template has an image
              " image.id||$notnull",
              storeIds.length ? `stores.id||$in||${storeIds.join(",")}` : "",
            ].filter(Boolean);
            const specificRes =
              await adminApiService.getManyBaseGfFigmaTemplateDataAdminControllerGfFigmaTemplateData(
                undefined,
                undefined,
                filterSpecific,
                undefined,
                ["updatedAt,DESC"],
                Array.from(new Set([...(relations || []), "stores"])),
                500,
                0,
                1,

                undefined
              );
            specific = specificRes.data || [];
            specificLoaded = true;
          } catch (e) {
            // Non-fatal; proceed with public templates
            specific = [];
            specificLoaded = true;
          }
        }

        // Phase 2: fetch public templates (paginated)
        const publicFilters: string[] = [
          "type||$eq||public",
          // Ensure template has an image
          " image.id||$notnull",
        ];
        const publicRes =
          await adminApiService.getManyBaseGfFigmaTemplateDataAdminControllerGfFigmaTemplateData(
            undefined,
            undefined,
            publicFilters,
            undefined,
            ["updatedAt,DESC"],
            relations,
            pageSize,
            0,
            page,
            undefined
          );

        setState((prev) => {
          const publicIncoming = publicRes.data || [];
          const publicItems =
            page === initialPage || options?.reset
              ? publicIncoming
              : [
                  ...prev.items.filter(
                    (i) =>
                      (i as any).type ===
                        (GfFigmaTemplateData as any).TypeEnum?.Public ||
                      (i as any).type === "public"
                  ),
                  ...publicIncoming,
                ];
          const combinedMap = new Map<string, GfFigmaTemplateData>();
          specific.forEach((t) => combinedMap.set(t.id, t));
          publicItems.forEach((t) => combinedMap.set(t.id, t));
          const combined = Array.from(combinedMap.values());
          const totalPublic = publicRes.total || publicItems.length;
          const totalLoadedPublic = publicItems.length;
          return {
            ...prev,
            loading: false,
            items: combined,
            specific,
            specificLoaded,
            page,
            total: totalPublic,
            hasMore: totalLoadedPublic < totalPublic,
            error: undefined,
          };
        });
      } catch (e: any) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: e?.message || "Failed to load templates",
        }));
      }
    },
    [
      state.specific,
      state.specificLoaded,
      storeIds,
      relations,
      pageSize,
      initialPage,
    ]
  );

  const loadMore = () => {
    if (state.loading || !state.hasMore) return;
    fetchTemplates(state.page + 1);
  };

  const refresh = () => {
    setState((prev) => ({
      ...prev,
      items: [],
      page: initialPage,
      hasMore: true,
      specific: [],
      specificLoaded: false,
    }));
    setRefreshKey((k) => k + 1);
  };

  useEffect(() => {
    fetchTemplates(initialPage, { reset: true });
  }, [initialPage, refreshKey, storeIds?.join(",")]);

  return { ...state, selected, setSelected, loadMore, refresh };
};
